import { useTranslation } from 'react-i18next';

import { CardSkeletons } from '@todo/web-ui/components/card-skeletons';
import { CollectionGuide, useCollectionCopy } from '@todo/web-ui/components/collection-guide';
import { EventGrid } from '@todo/web-ui/components/event-grid';
import { NavLink } from '@todo/web-ui/components/nav-link';
import { Intro, Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { CardGrid, Section } from '@todo/web-ui/components/section';
import { seedScript } from '@todo/web-ui/components/seed-script';
import { TourGrid } from '@todo/web-ui/components/tour-grid';
import { RelatedGuides, relatedGuidesSeed } from '@todo/web-ui/components/related-guides';
import { localePath } from '@todo/web-ui/lib/format';
import { HUB_GUIDES, NYE_PATH } from '@/lib/collections';
import { NYE_EVENTS_SEED_KEY, NYE_TOURS_SEED_KEY, fetchNyeEvents, fetchNyeTours } from '@/lib/prefetch';
import { useSeededList } from '@/lib/use-seeded-list';
import { localizedTitle } from '@todo/core/i18n/event-content';
import { localizedTourTitle } from '@todo/core/i18n/tour-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import { absoluteUrl, breadcrumbJsonLd, faqPageJsonLd, itemListJsonLd, seoTitle } from '@todo/core/seo';
import type { DbEvent } from '@todo/core/supabase';
import type { Tour } from '@todo/core/tours';

// New Year's Eve in Dubai — one hub for the night, at a URL without a year so
// it keeps what it earns from one season to the next: the NYE shows of the
// events list (parties, gala dinners, concerts — picked by title and date) and
// the New Year cruises among the tours (by title; src/lib/prefetch.ts), then a
// guide (where to watch the fireworks, with children, roads and metro, when to
// book) and an FAQ from `collections.new-years-eve` in the resources, as on
// /boats. Indexable all year: the copy answers the search, the lists come and
// go with the season — from 2 January until the next season's events go on
// sale the events block says so and links to /events, and the cruise block is
// left out while there's none.
export default function NewYearsEve() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { rows: events, loading: eventsLoading } = useSeededList<DbEvent>(NYE_EVENTS_SEED_KEY, fetchNyeEvents);
  const { rows: tours } = useSeededList<Tour>(NYE_TOURS_SEED_KEY, fetchNyeTours);
  const copy = useCollectionCopy(NYE_PATH);
  const title = t('collections.new-years-eve.title');

  const items = [
    ...events.map((e) => ({ name: localizedTitle(e, locale), url: absoluteUrl(locale, `events/${e.show_slug}`) })),
    ...tours.map((tour) => ({ name: localizedTourTitle(tour, locale), url: absoluteUrl(locale, `tours/${tour.id}`) })),
  ];

  return (
    <Page>
      <PageHead
        title={seoTitle(t('collections.new-years-eve.seoTitle'))}
        description={t('collections.new-years-eve.meta')}
        path={NYE_PATH}
        indexable>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd(locale, [{ name: title }]))}</script>
        {items.length ? (
          <script type="application/ld+json">{JSON.stringify(itemListJsonLd(items, absoluteUrl(locale, NYE_PATH)))}</script>
        ) : null}
        {copy.faq.length ? <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(copy.faq))}</script> : null}
        {seedScript(NYE_EVENTS_SEED_KEY)}
        {seedScript(NYE_TOURS_SEED_KEY)}
        {relatedGuidesSeed(NYE_PATH, locale)}
      </PageHead>

      <Section id="nye-title" level={1} title={t('collections.new-years-eve.heading')}>
        <Intro>{t('collections.new-years-eve.intro')}</Intro>
      </Section>

      <Section
        id="nye-events"
        title={t('collections.new-years-eve.eventsTitle')}
        more={{ href: localePath(locale, 'events'), label: t('home.viewAllEvents') }}>
        {events.length ? (
          <EventGrid events={events} />
        ) : eventsLoading ? (
          <CardGrid>
            <CardSkeletons />
          </CardGrid>
        ) : (
          <Intro>
            {t('collections.new-years-eve.eventsOff')}{' '}
            <NavLink href={localePath(locale, 'events')}>{t('collections.new-years-eve.eventsOffLink')}</NavLink>
          </Intro>
        )}
      </Section>

      {tours.length ? (
        <Section
          id="nye-cruises"
          title={t('collections.new-years-eve.toursTitle')}
          more={{ href: localePath(locale, 'boats'), label: t('collections.new-years-eve.toursMore') }}>
          <TourGrid tours={tours} />
        </Section>
      ) : null}

      <CollectionGuide slug={NYE_PATH} copy={copy} />

      <RelatedGuides page={NYE_PATH} guides={HUB_GUIDES[NYE_PATH]} />
    </Page>
  );
}
